import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Tag as TagIcon, Trash2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Popover, PopoverAnchor, PopoverContent } from "../../components/ui/popover";
import { type Annotation, api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";
import {
  type HighlightRange,
  firstAnnotationQuote,
  highlightKey,
  labelForKind,
} from "./annotations-utils";

interface Props {
  docId: string;
  versionId: string;
  pendingAnnotations?: Annotation[];
}

interface OpenPopover {
  kind: "comment" | "tag";
  blockKey: string;
  rect: DOMRect;
  annotations: Annotation[];
}

const EMPTY_ANNOTATIONS: Annotation[] = [];

/**
 * Readwise-style annotations layer.
 *
 * Highlights render as inline `<mark>` only — no margin chip. Comments and
 * tags render the `<mark>` plus a discrete icon inserted at the end of the
 * containing block. Clicking an icon opens an inline Radix popover anchored
 * to the icon's rect. A left gutter is rendered only when the page has at
 * least one comment or tag.
 *
 * Highlight wrapping uses DOM Range API and must respect the invariants in
 * docs/document-viewer-annotations.md: anchor to leaf `data-block-id`,
 * never mutate the source Markdown, re-anchor via `quote` when offsets drift.
 */
export function AnnotationsLayer({
  docId,
  versionId,
  pendingAnnotations = EMPTY_ANNOTATIONS,
}: Props) {
  const qc = useQueryClient();
  // B1.4 — keep annotations stable across focus / scroll. The cache is the
  // source of truth; setQueryData from createAnnotation populates it.
  const { data } = useQuery({
    queryKey: ["doc-annotations", docId],
    queryFn: () => api.listAnnotations(docId),
    enabled: !!docId && !!versionId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const [open, setOpen] = useState<OpenPopover | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const deleteAnnotation = useMutation({
    mutationFn: (id: string) => api.deleteAnnotation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["doc-annotations", docId] });
      toast.success("Anotación eliminada");
      setOpen(null);
    },
    onError: (err) => toast.error(`No se pudo eliminar: ${err}`),
  });

  const annotations = useMemo<Annotation[]>(
    () => [...(data?.items ?? []), ...pendingAnnotations],
    [data, pendingAnnotations],
  );

  // B1.4 — bump this counter from a MutationObserver below whenever the
  // prose-root's innerHTML is replaced by React. That re-runs the paint
  // effect so the marks survive.
  const [paintTick, setPaintTick] = useState(0);

  useEffect(() => {
    const contentRoot = document.querySelector<HTMLElement>("[data-prose-root]");
    if (!contentRoot) return;
    const observer = new MutationObserver((records) => {
      // We only care about whole-subtree replacements (childList changes on
      // the root itself), not the marks we add. If our own marks change we'd
      // get into a loop.
      const meaningful = records.some(
        (r) =>
          r.target === contentRoot &&
          r.type === "childList" &&
          // Ignore mutations that only added/removed `mark[data-ann-highlight]`
          // or `[data-ann-icon]` — those are *our* writes.
          ![...r.addedNodes, ...r.removedNodes].every(
            (n) =>
              n instanceof HTMLElement &&
              (n.matches?.("mark[data-ann-highlight], [data-ann-icon]") ?? false),
          ),
      );
      if (meaningful) setPaintTick((n) => n + 1);
    });
    observer.observe(contentRoot, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    // Read paintTick so biome/react-hooks track it as a meaningful dep.
    void paintTick;
    const contentRoot = document.querySelector<HTMLElement>("[data-prose-root]");
    if (!contentRoot) return;

    // 1. clear existing marks + injected icons from previous renders
    for (const node of contentRoot.querySelectorAll("mark[data-ann-highlight]")) {
      const parent = node.parentNode;
      while (node.firstChild) parent?.insertBefore(node.firstChild, node);
      node.remove();
      parent?.normalize();
    }
    for (const node of contentRoot.querySelectorAll("[data-ann-icon]")) {
      node.remove();
    }

    if (annotations.length === 0) {
      setOpen(null);
      return;
    }

    // 2. group annotations by block
    const grouped = new Map<string, Annotation[]>();
    for (const a of annotations) {
      try {
        const r = JSON.parse(a.range_json) as { blockId: string };
        if (!r.blockId) continue;
        const arr = grouped.get(r.blockId) ?? [];
        arr.push(a);
        grouped.set(r.blockId, arr);
      } catch {
        /* ignore */
      }
    }

    const resolvedGroups = new Map<HTMLElement, Annotation[]>();
    for (const [blockId, anns] of grouped) {
      const block = findAnnotationBlock(contentRoot, blockId, anns);
      if (!block) continue;
      const arr = resolvedGroups.get(block) ?? [];
      arr.push(...anns);
      resolvedGroups.set(block, arr);
    }

    // 3. apply highlights (highlight kind = pure <mark>; comment/tag = <mark> with data attributes)
    for (const [block, anns] of resolvedGroups) {
      const byKey = new Map<string, { annotation: Annotation; range: HighlightRange }>();
      for (const a of anns) {
        try {
          const range = JSON.parse(a.range_json) as HighlightRange;
          byKey.set(`${a.id}:${highlightKey(range)}`, { annotation: a, range });
        } catch {
          /* ignore */
        }
      }
      const ordered = Array.from(byKey.values()).sort(
        (x, y) => y.range.charStartInBlock - x.range.charStartInBlock,
      );
      for (const { annotation, range } of ordered) {
        applyAnnotationMark(block, annotation, range);
      }

      // 4. append icons for comment/tag at end of block
      const commentIds = anns.filter((a) => a.kind === "comment").map((a) => a.id);
      const tagAnns = anns.filter((a) => a.kind === "tag");
      const tagIds = tagAnns.map((a) => a.id);
      if (commentIds.length > 0) appendIcon(block, "comment", commentIds.length);
      if (tagIds.length > 0) appendIcon(block, "tag", tagIds.length);
      // C1.1 — chip "#tagname" en gutter derecho (no inline)
      const tagNames = tagAnns.map((a) => a.body ?? "").filter(Boolean);
      if (tagNames.length > 0) appendMarginTagChips(block, tagNames);

      block.setAttribute("data-ann-block-key", block.dataset.blockId ?? "block");
    }

    return () => {
      for (const node of document.querySelectorAll("[data-ann-icon]")) node.remove();
      for (const node of document.querySelectorAll(".tag-margin-chip-wrap")) node.remove();
    };
  }, [annotations, paintTick]);

  // delegated click handler for icons
  useEffect(() => {
    function handler(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      const icon = target?.closest<HTMLElement>("[data-ann-icon]");
      if (!icon) return;
      e.preventDefault();
      e.stopPropagation();
      const kind = icon.dataset.annIcon as "comment" | "tag";
      const blockKey = icon.dataset.annBlockKey ?? "";
      if (!kind || !blockKey) return;
      const rect = icon.getBoundingClientRect();
      const blockAnns = annotations.filter((a) => {
        try {
          const r = JSON.parse(a.range_json) as { blockId: string };
          return r.blockId === blockKey && a.kind === kind;
        } catch {
          return false;
        }
      });
      if (blockAnns.length === 0) return;
      setOpen({ kind, blockKey, rect, annotations: blockAnns });
    }
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [annotations]);

  // re-position anchor when scrolling
  useEffect(() => {
    if (!open) return;
    function recompute() {
      const blockKey = open?.blockKey;
      const kind = open?.kind;
      if (!blockKey || !kind) return;
      const icon = document.querySelector<HTMLElement>(
        `[data-ann-icon='${kind}'][data-ann-block-key='${blockKey}']`,
      );
      if (icon) setOpen((p) => (p ? { ...p, rect: icon.getBoundingClientRect() } : null));
    }
    const mainEl = document.getElementById("doc-main-content");
    mainEl?.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      mainEl?.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, [open]);

  const closePopover = useCallback(() => setOpen(null), []);

  return (
    <>
      <AnnotationsGutter annotations={annotations} />
      <div ref={overlayRef} data-ann-overlay aria-hidden="true" />
      {open && (
        <Popover open onOpenChange={(o) => !o && closePopover()}>
          <PopoverAnchor asChild>
            <div
              style={{
                position: "fixed",
                top: open.rect.top,
                left: open.rect.left,
                width: open.rect.width,
                height: open.rect.height,
                pointerEvents: "none",
              }}
            />
          </PopoverAnchor>
          <PopoverContent
            align="start"
            side="bottom"
            className="w-80 max-w-[24rem]"
            data-ann-popover={open.kind}
          >
            <AnnotationPopoverBody
              open={open}
              onDelete={(id) => deleteAnnotation.mutate(id)}
              pending={deleteAnnotation.isPending}
            />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}

function AnnotationsGutter({ annotations }: { annotations: Annotation[] }) {
  const [markers, setMarkers] = useState<{ id: string; top: number; kind: "comment" | "tag" }[]>(
    [],
  );

  useLayoutEffect(() => {
    const hasCommentOrTag = annotations.some((a) => a.kind === "comment" || a.kind === "tag");
    if (!hasCommentOrTag) {
      setMarkers([]);
      return;
    }
    const proseEl = document.querySelector<HTMLElement>("[data-prose]");
    if (!proseEl) return;
    const hostRect = proseEl.getBoundingClientRect();
    const items: { id: string; top: number; kind: "comment" | "tag" }[] = [];
    for (const a of annotations) {
      if (a.kind === "highlight") continue;
      try {
        const r = JSON.parse(a.range_json) as { blockId: string };
        const block = proseEl.querySelector<HTMLElement>(`[data-block-id='${r.blockId}']`);
        if (!block) continue;
        const rect = block.getBoundingClientRect();
        const top = rect.top - hostRect.top + 4;
        items.push({ id: a.id, top, kind: a.kind as "comment" | "tag" });
      } catch {
        /* skip */
      }
    }
    setMarkers(items);
  }, [annotations]);

  if (markers.length === 0) return null;

  return (
    <div
      data-ann-gutter
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: -28,
        width: 24,
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {markers.map((m) => (
        <div
          key={m.id}
          style={{
            position: "absolute",
            top: m.top,
            left: 0,
            width: 4,
            height: 16,
            borderRadius: 2,
            background: m.kind === "comment" ? "var(--accent-400)" : "var(--accent-300)",
          }}
        />
      ))}
    </div>
  );
}

function AnnotationPopoverBody({
  open,
  onDelete,
  pending,
}: {
  open: OpenPopover;
  onDelete: (id: string) => void;
  pending: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <header className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {open.kind === "comment" ? (
          <>
            <MessageSquare size={12} /> Comentarios ({open.annotations.length})
          </>
        ) : (
          <>
            <TagIcon size={12} /> Etiquetas ({open.annotations.length})
          </>
        )}
      </header>
      <ul className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
        {open.annotations.map((a) => (
          <AnnotationItem
            key={a.id}
            annotation={a}
            kind={open.kind}
            onDelete={onDelete}
            pending={pending}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * C1.2 — Annotation item con texto plegable. Si el body > 240 chars,
 * muestra preview + "Ver todo / Ver menos".
 */
function AnnotationItem({
  annotation: a,
  kind,
  onDelete,
  pending,
}: {
  annotation: Annotation;
  kind: "comment" | "tag";
  onDelete: (id: string) => void;
  pending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const fullBody = kind === "tag" ? `#${a.body ?? "tag"}` : (a.body ?? "");
  const LIMIT = 240;
  const isLong = fullBody.length > LIMIT;
  const visible = isLong && !expanded ? `${fullBody.slice(0, LIMIT)}…` : fullBody;
  return (
    <li
      data-testid="annotation-item"
      data-expanded={expanded ? "true" : "false"}
      className="rounded-md border border-(--border-subtle) bg-(--bg-base) p-2 text-xs"
    >
      <div className="flex items-baseline justify-between gap-2">
        <strong className="text-(--text-primary) text-[11px]">{a.author_subject}</strong>
        <span className="text-(--text-tertiary) text-[10px]">{formatRelative(a.created_at)}</span>
      </div>
      <p
        className="mt-1 whitespace-pre-wrap text-(--text-secondary)"
        style={{ wordBreak: "break-word" }}
        data-testid="annotation-body"
      >
        {visible}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="annotation-toggle"
          className="mt-0.5 text-[10px] text-(--accent-600) hover:underline"
        >
          {expanded ? "Ver menos" : "Ver todo"}
        </button>
      )}
      <div className="mt-1">
        <button
          type="button"
          aria-label="Eliminar anotación"
          className="inline-flex items-center gap-1 text-[10px] text-(--text-tertiary) hover:text-(--error)"
          disabled={pending}
          onClick={() => onDelete(a.id)}
        >
          <Trash2 size={11} /> Eliminar
        </button>
      </div>
    </li>
  );
}

function appendIcon(block: HTMLElement, kind: "comment" | "tag", count: number): void {
  const existing = block.querySelector<HTMLElement>(`[data-ann-icon='${kind}']`);
  if (existing) {
    existing.dataset.annCount = String(count);
    existing.title = labelForKind(kind, count);
    return;
  }
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("data-ann-icon", kind);
  btn.setAttribute("data-ann-count", String(count));
  btn.setAttribute("data-ann-block-key", block.dataset.blockId ?? "");
  btn.setAttribute("aria-label", labelForKind(kind, count));
  btn.title = labelForKind(kind, count);
  btn.style.cssText =
    "display:inline-flex;align-items:center;gap:2px;margin:0 0 0 6px;padding:0 4px;height:18px;border-radius:4px;background:var(--bg-elevated);border:1px solid var(--border-subtle);font-size:10px;color:var(--text-tertiary);cursor:pointer;vertical-align:baseline;line-height:1;font-family:var(--font-display);user-select:none;";
  btn.innerHTML =
    kind === "comment"
      ? `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg><span>${count}</span>`
      : `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7" cy="7" r="1"/></svg><span>${count}</span>`;
  block.appendChild(document.createTextNode(" "));
  block.appendChild(btn);
}

function findAnnotationBlock(
  contentRoot: HTMLElement,
  blockId: string,
  annotations: Annotation[],
): HTMLElement | null {
  const quote = firstAnnotationQuote(annotations);
  const direct = contentRoot.querySelector<HTMLElement>(`[data-block-id="${blockId}"]`);
  if (direct) return findLeafBlockByQuote(direct, quote) ?? direct;
  if (!quote) return null;
  return findLeafBlockByQuote(contentRoot, quote);
}

function findLeafBlockByQuote(root: HTMLElement, quote: string | null): HTMLElement | null {
  if (!quote) return null;
  return (
    Array.from(
      root.querySelectorAll<HTMLElement>(
        "li[data-block-id], p[data-block-id], h1[data-block-id], h2[data-block-id], h3[data-block-id], h4[data-block-id], h5[data-block-id], h6[data-block-id], pre[data-block-id], blockquote[data-block-id], table[data-block-id]",
      ),
    ).find((node) => (node.textContent ?? "").includes(quote)) ?? null
  );
}

function applyAnnotationMark(
  block: HTMLElement,
  annotation: Annotation,
  range: HighlightRange,
): void {
  const text = block.textContent ?? "";
  let start = range.charStartInBlock;
  let end = range.charEndInBlock;
  if (range.quote && (start < 0 || end <= start || text.slice(start, end) !== range.quote)) {
    const idx = text.indexOf(range.quote);
    if (idx >= 0) {
      start = idx;
      end = idx + range.quote.length;
    }
  }
  if (start < 0 || end <= start) return;

  const domRange = rangeFromTextOffsets(block, start, end);
  if (!domRange) return;
  if (domRange.toString().trim().length === 0) return;
  const mark = document.createElement("mark");
  mark.dataset.annHighlight = "1";
  if (annotation.kind === "highlight") {
    mark.dataset.color = annotation.color ?? "yellow";
  } else if (annotation.kind === "comment") {
    mark.dataset.commentId = annotation.id;
  } else if (annotation.kind === "tag") {
    mark.dataset.tag = annotation.body ?? "";
  }
  try {
    domRange.surroundContents(mark);
  } catch {
    const frag = domRange.extractContents();
    mark.appendChild(frag);
    domRange.insertNode(mark);
  }
  if ((mark.textContent ?? "").trim().length === 0) {
    const parent = mark.parentNode;
    while (mark.firstChild) parent?.insertBefore(mark.firstChild, mark);
    mark.remove();
    parent?.normalize();
  }
}

function rangeFromTextOffsets(root: HTMLElement, start: number, end: number): Range | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return node.parentElement?.closest("[data-ann-icon]")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT;
    },
  });
  const range = document.createRange();
  let seen = 0;
  let didStart = false;
  let didEnd = false;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const length = node.textContent?.length ?? 0;
    const next = seen + length;
    if (!didStart && start <= next) {
      range.setStart(node, Math.max(0, start - seen));
      didStart = true;
    }
    if (!didEnd && end <= next) {
      range.setEnd(node, Math.max(0, end - seen));
      didEnd = true;
      break;
    }
    seen = next;
  }

  return didStart && didEnd ? range : null;
}

/**
 * C1.1 — Tag chips al margen derecho del bloque (no inline).
 *
 * Inserta un `<span class="tag-margin-chip-wrap">` flotado a la derecha
 * con un chip `#tagname` por cada tag del bloque. Vive dentro del propio
 * bloque (sin posicionamiento absoluto) y se elimina en el cleanup del
 * effect.
 */
function appendMarginTagChips(block: HTMLElement, tags: string[]): void {
  const existing = block.querySelector<HTMLElement>(".tag-margin-chip-wrap");
  if (existing) existing.remove();

  const wrap = document.createElement("span");
  wrap.className = "tag-margin-chip-wrap";
  wrap.setAttribute("aria-hidden", "false");
  wrap.style.cssText =
    "float:right;display:inline-flex;flex-wrap:wrap;gap:4px;margin:0 0 0 12px;vertical-align:top;max-width:240px;";
  for (const tag of tags) {
    const chip = document.createElement("span");
    chip.className = "tag-margin-chip";
    chip.dataset.tag = tag;
    chip.textContent = `#${tag}`;
    wrap.appendChild(chip);
  }
  // Insert at the start of the block so it floats to the right of the first
  // line — keeps the visual close to the underlined text.
  block.insertBefore(wrap, block.firstChild);
}
